/* eslint-disable camelcase */
import { put, call, select } from 'redux-saga/effects';
import * as types from '../../constants/ActionTypes';
import { deleteToken, getToken } from '../../functions/tokenUtils';
import Api from '../../functions/Api';
import sendEvent from '../../functions/analytics';
import { getCookie } from '../../functions/manageCookie';
import {
  clearHijackSession,
  isHijackSession,
  storeLoginUrl,
} from '../../functions/authV1';
import { currencyMapping } from './usersConstants';

export function resolveNextLoginPath(action) {
  let nextPath =
    action.nextLink || window.location.pathname + window.location.search;
  if (nextPath) {
    try {
      const nextUrl = new URL(nextPath, window.location.origin);
      if (nextUrl.origin === window.location.origin) {
        nextPath = nextUrl.pathname + nextUrl.search;
      }
    } catch (e) {
      if (nextPath.startsWith(window.location.origin)) {
        nextPath = nextPath.replace(window.location.origin, '');
      }
    }
  }
  return nextPath;
}

export function* requestUser(action) {
  try {
    yield put({
      type: types.FETCH_DEFAULT_COUNTRY,
    });

    if (process.env.NODE_ENV === 'development') {
      const token = getToken();
      const tokenFromCookie = getCookie('token');
      const tokenFromStorage = localStorage.getItem('token');
      // eslint-disable-next-line no-console
      console.log('[Auth Debug] Requesting user profile:', {
        tokenExists: !!token,
        tokenFromCookie: !!tokenFromCookie,
        tokenFromStorage: !!tokenFromStorage,
        tokenLength: token ? token.length : 0,
        cookieValue: tokenFromCookie
          ? `${tokenFromCookie.substring(0, 20)}...`
          : 'missing',
        storageValue: tokenFromStorage
          ? `${tokenFromStorage.substring(0, 20)}...`
          : 'missing',
        allCookies: document.cookie,
        localStorageKeys: Object.keys(localStorage),
        currentUrl: window.location.href,
      });

      if (!token) {
        // eslint-disable-next-line no-console
        console.error(
          '[Auth Debug] CRITICAL: No token found when requesting user profile!',
          {
            cookieExists: !!tokenFromCookie,
            localStorageExists: !!tokenFromStorage,
            allCookies: document.cookie,
            localStorageKeys: Object.keys(localStorage),
          }
        );
      }
    }

    const {
      data,
      data: { isSales, isPartner },
    } = yield call(Api.get, 'profile/');
    if (isHijackSession() && !data.isHijacked) clearHijackSession();

    sendEvent('identify', data.id, {
      phone: data.phone,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      username: data.nickname,
    });

    yield put({
      type: types.RECEIVE_USER,
      payload: { ...data, currencyCode: currencyMapping[data.locale] },
    });

    yield put({
      type: types.RECEIVE_USER_INFO,
    });

    yield put({
      type: types.FETCH_LANGUAGES,
      locale: data.locale,
    });

    if (data.requisites === null) {
      yield put({
        type: types.ADD_REQUISITES,
        data: { user: data.id },
      });
    }

    let locale = yield select((state) => state.user.locale);

    while (!locale) {
      locale = yield select((state) => state.user.locale);
    }

    yield put({
      type: types.FETCH_CURRENCIES,
      locale,
    });

    if (isSales) {
      yield put({
        type: types.FETCH_REPORTS_SUMMARY,
        role: 'affiliate',
      });
    }

    if (isPartner) {
      yield put({
        type: types.FETCH_AFFILIATE_PARTNER,
      });
      yield put({
        type: types.FETCH_REPORTS_SUMMARY,
        role: 'author',
      });
      yield put({
        type: types.FETCH_PARTNER_DATA,
      });
    }

    yield put({
      type: types.REQUEST_QUESTS_STATS,
      page: 1,
    });
  } catch (error) {
    const { status = 0 } = error;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[Auth Debug] requestUser error:', {
        status,
        message: error.message,
        tokenExists: !!getToken(),
        url: error.url || 'profile/',
      });
    }
    if (status === 401 || status === 500) {
      clearHijackSession();
      deleteToken();

      const nextPath = resolveNextLoginPath(action);

      yield put({
        type: types.LOGOUT,
        returnUrl: nextPath
          ? storeLoginUrl(nextPath)
          : action.returnUrl || storeLoginUrl(),
        email: '',
        isHardRedirect: true,
      });
      return;
    }
    if (!action.silent) {
      yield put({ type: types.NETWORK_ERROR, error });
    }
  }
}
